import ReactGA from 'react-ga4'
import React, { useMemo, useState, useEffect, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  ScrollRestoration,
  useNavigate,
  useParams,
  Navigate,
} from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async';
import SEO from './components/SEO'
import { generateWebsiteSchema } from './services/schemaGenerators'
import {
  Box,
  CssBaseline,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material'
import './services/interceptor'

// import List from './components/laws/List'
// import DecisionList from './components/decisions/DecisionList'
// import GuideList from './components/guides/GuideList'
// import PDFViewer from './components/pdf-viewer/PDFViewer'
// import GlobalSearchInLawSSP from './components/globalSearch/GlobalSearchInLawSSP'
// import RenderHTML from './components/RenderHTML'
// import Disclaimer from './components/Disclaimer'
// import Waitlist from './components/landingPage/Waitlist'
// import UniversalSearchListDocs from './components/UniversalSearchListDocs'
// import UniversalSearchGCC from './components/UniversalSearchGCC'
// import UniversalSearchMetrics from './components/UniversalSearchMetrics'
// import LawDetailsPage from './components/LawDetailsPage'
// import ArticleDetailPage from './components/ArticleDetailPage'
// import ArticleDetailPageV2 from './components/ArticleDetailPageV2'
// import ArticleDetailPageV3 from './components/ArticleDetailPageV3'
// import ArticleDetailPageV4 from './components/ArticleDetailPageV4'

import usePageTracking from './services/usePageTracking'

// Keep critical components loaded immediately (for better SEO)
import ArticleDetail from './components/laws/ArticleDetail'
import DecisionDetail from './components/decisions/DecisionDetail'
import GuideDetail from './components/guides/GuideDetail'
import TaxTreatyDetail from './components/taxTreaty/TaxTreatyDetail'
// import Error from './components/Error'
import Error404 from './components/Error404';
import NavBar from './components/homepage2/components/NavBar'
import Footer from './components/homepage2/components/Footer'
import WarningPopup from './components/WarningPopup'
import ThankYou from './components/ThankYou'

import AuthCallbackHandler from './context/AuthCallbackHandler'
import { SupabaseProvider, useSupabase } from './context/SupabaseContext'

// Import the CountryProvider so that we dont need to hard countries in navbar links
import { CountryLawProvider } from './context/CountryLawContext'

// Keep law structure pages immediate - they're important for SEO
import HierarchicalArticlesView from './components/laws/HierarchicalArticlesView'
import HierarchicalLawStructure from './components/laws/HierarchicalLawStructure'

// Lazy load non-critical components (won't affect SEO)
const Homepage = React.lazy(() =>
  import('./components/homepage2/pages/Homepage')
)
const Register = React.lazy(() => import('./components/Register'))
const CookiePolicy = React.lazy(() => import('./components/CookiePolicy'))
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'))
const TermsAndConditions = React.lazy(() =>
  import('./components/TermsAndConditions')
)
const PartnerWithUs = React.lazy(() => import('./components/PartnerWithUs'))
const AboutUs = React.lazy(() => import('./components/AboutUs'))
const ContactUs = React.lazy(() => import('./components/ContactUs'))
const Features = React.lazy(() => import('./components/Features'))
const GlobalSearchInLawCSP = React.lazy(() =>
  import('./components/globalSearch/GlobalSearchInLawCSP')
)
const TaxTreatyList = React.lazy(() =>
  import('./components/taxTreaty/TaxTreatyList')
)

const Root = () => {
  usePageTracking()
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <CssBaseline />
      <WarningPopup />
      {/* <Container>
      <Typography variant="h2">GCC Laws Search Portal</Typography>
    </Container> */}
      <NavBar />
      {/* We can't see footer unless we scroll once - to make these changes we've added such code */}

      {/* Main content area with minimum height to ensure scrolling */}
      <Box
        sx={{
          flex: 1,
          minHeight: '100vh', // 'calc(100vh + 1px)' Ensures content is just taller than viewport
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Outlet wrapper to handle content */}
        <Box
          sx={{
            flex: 1,
            minHeight: '100%', // Take up full height of parent
          }}
        >
          <Outlet />
        </Box>
        {/* Optional: Add white space if content is insufficient */}
        {/* <Box
          sx={{
            flexGrow: 1,
            minHeight: 0, // Allow it to grow but not shrink below 0
          }}
        /> */}

        <ScrollRestoration />
      </Box>
      <Footer />
    </Box>
  )
}
// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate()
  const { user, loading } = useSupabase()
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: '#232536',
          textAlign: 'center',
        }}
      >
        <CircularProgress
          sx={{
            color: '#ffda1b',
            marginBottom: '20px',
          }}
        />
        <Typography
          variant='h6'
          sx={{
            color: 'white',
          }}
        >
          Checking authentication...
        </Typography>
      </Box>
    )
  }
  if (!user) {
    return <Navigate to='/register' replace />
  }
  return children
}
const initializeGA = () => {
  const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID

  if (gaId) {
    ReactGA.initialize(gaId, {
      debug: process.env.REACT_APP_ENVIRONMENT === 'dev',
      // Optional: Add other configuration options
      // gtagOptions: {
      //   send_page_view: true, // fasle, if you want to manually control page views
      // },
    })
  } else {
    console.warn('Google Analytics measurement ID not found')
  }
}
// Loading component for Suspense
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '300px',
      gap: 2,
    }}
  >
    <CircularProgress sx={{ color: '#232536' }} />
    <Typography variant='body2' color='text.secondary'>
      Loading content...
    </Typography>
  </Box>
)

// SEO wrapper component for static pages
const StaticPageWrapper = ({
  children,
  title,
  description,
  canonical,
  keywords,
  ogImage = '/images/default-og-image.jpg',
}) => (
  <>
    <SEO
      title={title}
      description={description}
      canonical={canonical}
      keywords={keywords}
      schemaMarkup={generateWebsiteSchema()}
      ogImage={ogImage}
    />
    {children}
  </>
)
// Law structure page wrapper with dynamic SEO
const LawPageWrapper = ({ children }) => {
  const { country, lawShortName, path } = useParams()

  const generateLawSEO = () => {
    const countryName = country?.toUpperCase()
    const lawDisplayNamesObj = {
      'cit-fdl-47-of-2022': 'UAE CIT (FDL No 47 of 2022)',
      'cit-fdl-60-of-2023': 'UAE CIT (FDL No 60 of 2023)',
      'indirect-vat-8-of-2017': 'UAE VAT (FDL No 8 of 2017)',
      'gcc-vat-agreement': 'GCC VAT Agreement',
      'tp-fdl-28-of-2022': 'UAE Tax Procedures (FDL No 28 of 2022)',
      'indirect-excise-7-of-2017': 'UAE Excise Tax (FDL No 7 of 2017)',
      'gcc-excise-agreement': 'GCC Excise Agreement',
      'fatca': 'UAE Foreign Account Tax Compliance Act',
      'vat-law': 'KSA VAT Law',
      'income-tax-law': 'KSA Income Tax Law'
    }
    const lawDisplayName = lawDisplayNamesObj[lawShortName] || `${country} Tax Law`

    let title, description, canonical

    if (path) {
      // Specific section page
      title = `${path} | ${lawDisplayName} | ${countryName} | GCC Tax Laws`
      description = `Access ${path} of ${lawDisplayName} in ${countryName}. Complete legal text and analysis.`
      canonical = `https://gcctaxlaws.com/laws/${country}/${lawShortName}/${path}`
    } else {
      // Main law page
      title = `${lawDisplayName} | ${countryName} | GCC Tax Laws`
      description = `Complete ${lawDisplayName} for ${countryName}. Browse articles, definitions, and legal provisions.`
      canonical = `https://gcctaxlaws.com/laws/${country}/${lawShortName}`
    }

    return { title, description, canonical }
  }

  const seoData = generateLawSEO()

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        canonical={seoData.canonical}
        keywords={`${country} tax law, ${lawShortName}, GCC tax laws`}
        schemaMarkup={generateWebsiteSchema()}
      />
      {children}
    </>
  )
}
const TaxTreatyPageWrapper = ({ children }) => {
  const { country } = useParams()

  const generateTaxTreatySEO = () => {
    const countryName = country?.toUpperCase() || 'GCC'

    const title = `${countryName} Tax Treaties | Double Taxation Agreements | GCC Tax Laws`
    const description = `Access comprehensive ${countryName} tax treaties and double taxation agreements. Browse bilateral tax agreements, withholding tax rates, and treaty benefits for ${countryName}.`
    const canonical = `https://gcctaxlaws.com/search/${country}/Tax-Treaties`
    const keywords = `${country} tax treaties, double taxation agreement, bilateral tax treaty, withholding tax rates, treaty benefits, ${countryName} DTA, international tax`

    return { title, description, canonical, keywords }
  }

  const seoData = generateTaxTreatySEO()

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        canonical={seoData.canonical}
        keywords={seoData.keywords}
        schemaMarkup={generateWebsiteSchema()}
      />
      {children}
    </>
  )
}
const App = () => {
  const helmetContext = useMemo(() => ({}), [])
  useEffect(() => {
    initializeGA()
  }, [])
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/',
          element: <Root />,
          errorElement: <Error404 />,
          children: [
            // {
            //   path: '/api',
            //   element: (
            //     <Navigate
            //       to='/api/fintegerllp/decreehub/api/swagger-ui/index.html'
            //       replace
            //     />
            //   ),
            // },
            {
              path: '/',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='GCC Tax Laws | Premier GCC Tax Research Platform'
                    description='Access comprehensive GCC tax laws, decisions, and guidance. Premier platform for tax advisors, corporations, and academic institutions seeking accurate tax information.'
                    canonical='https://gcctaxlaws.com/'
                    keywords='GCC tax laws, UAE tax law, Saudi Arabia tax, Qatar tax, Kuwait tax, Bahrain tax, Oman tax, tax research, corporate tax, VAT law'
                    ogImage='/images/homepage-og.jpg'
                  >
                    <Homepage />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/register',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Register | GCC Tax Laws'
                    description='Create your account to access premium tax research features on GCC Tax Laws platform.'
                    canonical='https://gcctaxlaws.com/register'
                  >
                    <Register />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/auth/callback',
              element: <AuthCallbackHandler />,
            },
            {
              path: '/success',
              element: (
                <ProtectedRoute>
                  <ThankYou />
                </ProtectedRoute>
              ),
            },
            {
              path: '/search-across-law',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Search GCC Tax Laws | Global Tax Law Search'
                    description='Search across all GCC tax laws, articles, decisions, and guidance. Find relevant tax information quickly and efficiently across UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, and Oman.'
                    canonical='https://gcctaxlaws.com/search-across-law'
                    keywords='search tax laws, GCC tax search, UAE tax search, find tax articles, tax law search engine'
                  >
                    <GlobalSearchInLawCSP />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            // {
            //   path: '/search-across-country',
            //   element: <UniversalSearchMetrics />,
            // },
            {
              path: '/laws/:country/:lawShortName/:path',
              element: (
                <LawPageWrapper>
                  <HierarchicalArticlesView />
                </LawPageWrapper>
              ),
            },
            {
              path: '/laws/:country/:lawShortName',
              element: (
                <LawPageWrapper>
                  <HierarchicalLawStructure />
                </LawPageWrapper>
              ),
            },
            {
              path: '/search/:country/Tax-Treaties',
              element: (
                  <Suspense fallback={<PageLoader />}>
                    <TaxTreatyPageWrapper>
                      <TaxTreatyList />
                    </TaxTreatyPageWrapper>
                  </Suspense>
              ),
            },
            // Critical content pages - NOT lazy loaded for better SEO
            {
              path: '/articles/:slug',
              element: <ArticleDetail />,
            },

            {
              path: '/decisions/:slug',
              element: <DecisionDetail />,
            },
            {
              path: '/guidances/:slug',
              element: <GuideDetail />,
            },
            {
              path: '/tax-treaties/:slug',
              element: <TaxTreatyDetail />,
            },

            // Static pages with SEO
            {
              path: '/about-us',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='About Us | GCC Tax Laws'
                    description='Learn about GCC Tax Laws platform, our mission to provide comprehensive tax research tools for the GCC region. Trusted by tax professionals across the Middle East.'
                    canonical='https://gcctaxlaws.com/about-us'
                    keywords='about GCC tax laws, tax research platform, Middle East tax, GCC tax experts'
                  >
                    <AboutUs />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/partner-with-us',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Partner with Us | GCC Tax Laws'
                    description='Join our partnership program at GCC Tax Laws. Collaborate with universities, firms, consultants, and corporate teams to advance tax knowledge across the GCC region.'
                    canonical='https://gcctaxlaws.com/partner-with-us'
                    keywords='GCC tax partnerships, tax law collaboration, university partnerships, consulting partnerships, corporate tax training, GCC tax experts, revenue sharing, content partnerships'
                  >
                    <PartnerWithUs />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/contact-us',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Contact Us | GCC Tax Laws'
                    description='Get in touch with GCC Tax Laws team. Contact us for support, partnerships, or inquiries about our tax research platform. We are here to help.'
                    canonical='https://gcctaxlaws.com/contact-us'
                    keywords='contact GCC tax laws, tax research support, customer service, partnerships'
                  >
                    <ContactUs />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/features',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Features | GCC Tax Laws Platform'
                    description='Explore powerful features of GCC Tax Laws platform including advanced search, document analysis, tax compliance tools, and comprehensive legal databases.'
                    canonical='https://gcctaxlaws.com/features'
                    keywords='tax research features, advanced search, document analysis, compliance tools, legal database'
                  >
                    <Features />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/cookie-policy',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Cookie Policy | GCC Tax Laws'
                    description='Learn about how we use cookies on GCC Tax Laws platform to improve your browsing experience and provide personalized content.'
                    canonical='https://gcctaxlaws.com/cookie-policy'
                  >
                    <CookiePolicy />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/privacy-policy',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Privacy Policy | GCC Tax Laws'
                    description='Read our privacy policy to understand how we collect, use, and protect your information on GCC Tax Laws platform. Your privacy is important to us.'
                    canonical='https://gcctaxlaws.com/privacy-policy'
                  >
                    <PrivacyPolicy />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/terms-and-conditions',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Terms and Conditions | GCC Tax Laws'
                    description='Read our terms and conditions for using GCC Tax Laws platform and accessing our tax research services. Legal terms and user agreement.'
                    canonical='https://gcctaxlaws.com/terms-and-conditions'
                  >
                    <TermsAndConditions />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            // {
            //   path: '/deepsearch-across-country',
            //   element: <UniversalSearchListDocs />,
            // },
            // {
            //   path: '/search-across-gcc',
            //   element: <UniversalSearchGCC />,
            // },
            // {
            //   path: '/law/:lawSlug',
            //   element: <LawDetailsPage />,
            // },
            // {
            //   path: '/law/:lawId/article/:articleId',
            //   element: <ArticleDetailPage />,
            // },
            // {
            //   path: '/law/:lawId/articlev2/:articleId',
            //   element: <ArticleDetailPageV2 />,
            // },
            // {
            //   path: '/law/:lawId/articlev3/:articleId',
            //   element: <ArticleDetailPageV3 />,
            // },
            // {
            //   path: '/law/:lawId/articlev4/:articleId',
            //   element: <ArticleDetailPageV4 />,
            // },
            // {
            //   path: '/law/:lawId/guideline/:guidelineId',
            //   element: <div>Guideline Detail Page</div>,
            // },
            // {
            //   path: '/law/:lawId/decision/:decisionId',
            //   element: <div>Decision Detail Page</div>,
            // },

            {
              path: '*', // Add this catch-all route
              element: <Error404 />,
            },
            //            {
            //              path: '/pdf',
            //              element: <PDFViewer />,
            //            },
          ],
        },
      ]),
    []
  )
  return (
    <SupabaseProvider>
      <HelmetProvider context={helmetContext}>
        <CountryLawProvider>
          <RouterProvider router={router} />
        </CountryLawProvider>
      </HelmetProvider>
    </SupabaseProvider>
  )
}
export default App
